import { authClient } from './auth-client.js';
import { ApiError, getAvailableProviders, unlinkAccount } from './api.js';
import { staleSessionMessage } from './refusals.js';

/**
 * How this account can be got into: which providers are linked, and whether
 * there is a password at all.
 *
 * ── ONE CALL, TWO SCREENS ────────────────────────────────────────────
 *
 * `/list-accounts` returns one row per way in, and a password is a row like any
 * other with `providerId: 'credential'`. So the same answer serves the sign-in
 * methods section — which Jon asked for on 2026-08-29, because an account can
 * have Google, Microsoft AND Apple linked on one address and nothing said so —
 * and the Password section, which has to know whether to offer "change" or
 * "set".
 *
 * The second half is the bug that came with the first. An account created
 * through a provider has no credential row, and `/change-password` reads that
 * row to compare the current password against — so it refuses with
 * `CREDENTIAL_ACCOUNT_NOT_FOUND`, and the Password section was linking somebody
 * to a screen that could only fail.
 *
 * ── IT IS FETCHED ONCE, IN Security.tsx ──────────────────────────────
 *
 * Not by each section that wants it. Two sections asking the same question on
 * mount is two requests for one answer, and they can disagree — the Password
 * card saying "set one" beside a list that shows a password would be a screen
 * arguing with itself.
 */
export const CREDENTIAL = 'credential';

/**
 * One linked provider, with the id needed to remove it.
 *
 * `accountId` is OUR `account.id`, which is what `/unlink-account` takes in
 * better-auth 1.7.1 — not the provider id, and not the subject id at the
 * provider. Three plausible values are in scope here and only one of them
 * works, so the field is named after the row rather than after the provider.
 */
export type LinkedProvider = {
  accountId: string;
  providerId: string;
};

export type SignInMethods = {
  /** Is there a password to change? */
  hasPassword: boolean;
  /** Linked providers, the `credential` row removed. Order is the server's. */
  providers: LinkedProvider[];
};


/**
 * Reduce the account rows to the two facts the screens act on.
 *
 * Pure and exported so the behaviour worth pinning is testable without a
 * network: that a `credential` row is a password and not a provider. Reading
 * the list as "the providers this account uses" would tell a password user they
 * sign in with Credential.
 */
export function summariseAccounts(accounts: { id: string; providerId: string }[]): SignInMethods {
  return {
    hasPassword: accounts.some((a) => a.providerId === CREDENTIAL),
    providers: accounts
      .filter((a) => a.providerId !== CREDENTIAL)
      .map((a) => ({ accountId: a.id, providerId: a.providerId })),
  };
}

/**
 * Ask the server. Returns null when it cannot be reached.
 *
 * Null rather than an assumed answer, and the callers render differently for
 * it: the section says it cannot list them rather than showing an empty list,
 * which would state — falsely — that nothing is connected.
 */
export async function loadSignInMethods(): Promise<SignInMethods | null> {
  try {
    const { data, error } = await authClient.listAccounts();
    if (error || !data) return null;
    /**
     * NO CAST. Deliberate, and the point of RidgeWill/account#49.
     *
     * This read `data as { id: string; providerId: string }[]`, which told the
     * compiler the shape instead of asking the library for it — so a row that
     * stopped carrying `id` or `providerId` would have compiled and produced an
     * empty list at runtime. That is the same class of silent skew as the
     * `/unlink-account` body, and a cast is how it hides.
     *
     * Now that both sides are on 1.7.x the inferred row is usable directly, so
     * `summariseAccounts` takes what the client actually returns and a future
     * shape change is a type error here.
     *
     * Worth knowing while reading this: 1.7 scoped account identity by issuer,
     * so a row now carries BOTH `id` (our row) and `accountId` (the subject at
     * the provider). `summariseAccounts` maps `accountId: a.id` on purpose —
     * `/unlink-account` wants the row, not the subject.
     */
    return summariseAccounts(data);
  } catch {
    return null;
  }
}

/**
 * What to call a provider on screen.
 *
 * Unknown ids fall through to the raw value rather than being dropped. A
 * provider added to the auth service before this map catches up should read as
 * an odd label, not vanish — a list of sign-in methods that silently omits one
 * is worse than one with an ugly name in it, because the whole point of the
 * section is that it is complete.
 */
export function providerLabel(id: string): string {
  return PROVIDER_LABEL[id] ?? id;
}

const PROVIDER_LABEL: Record<string, string> = {
  google: 'Google',
  microsoft: 'Microsoft',
  apple: 'Apple',
};

/** "Google", "Google and Microsoft", "Google, Microsoft and Apple". */
export function providerNames(ids: string[]): string {
  const names = ids.map(providerLabel);
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/**
 * Remove a linked provider.
 *
 * Returns `null` on success, or a message to show. Never throws: the caller is
 * a button, and an unhandled rejection there leaves the row spinning forever.
 *
 * ── The two refusals worth naming ────────────────────────────────────
 *
 * `SESSION_NOT_FRESH` is the one people will actually hit. `/unlink-account`
 * carries better-auth's `freshSessionMiddleware`, and the service sets no
 * `freshAge`, so the 24h default applies — and this portal is exactly the kind
 * of tab somebody leaves open for days. The raw message does not say what to
 * do about it, so this does.
 *
 * The sentence comes from `refusals.ts` because `/change-email` hit the same
 * wall (account#54): the auth service applies the same freshness bar there by
 * hand, and two screens writing their own version of "sign in again" is two
 * answers to one situation with only one of them ever getting updated.
 *
 * `FAILED_TO_UNLINK_LAST_ACCOUNT` should be unreachable, because the section
 * does not draw a remove control for the last row. It is still translated
 * rather than passed through: "unreachable" depends on the client's count
 * agreeing with the server's, and the day those disagree is the day somebody
 * needs the message to make sense.
 */
export async function unlinkProvider(accountId: string): Promise<string | null> {
  try {
    await unlinkAccount(accountId);
    return null;
  } catch (e) {
    if (e instanceof ApiError) {
      if (e.code === 'SESSION_NOT_FRESH' || e.status === 403) {
        return staleSessionMessage('removing a sign-in method');
      }
      if (e.code === 'FAILED_TO_UNLINK_LAST_ACCOUNT') {
        return 'This is the only sign-in method that can be removed here. Set a password or connect another provider first.';
      }
      return e.message;
    }
    return 'That sign-in method could not be removed just now. Nothing has changed.';
  }
}

/**
 * Which providers this deployment can offer at all. Null when it cannot be
 * asked.
 *
 * Null rather than an empty array, and the section renders the two
 * differently: an empty list means "every provider is already connected", and
 * drawing that for a failed request would withdraw the only control for adding
 * one on the strength of a network error. The failure direction is to keep
 * offering it.
 */
export async function loadAvailableProviders(): Promise<string[] | null> {
  try {
    const out = await getAvailableProviders();
    return Array.isArray(out.providers) ? out.providers : null;
  } catch {
    return null;
  }
}

/**
 * Is there anything left to connect?
 *
 * Pure, and it takes the unknown case explicitly: `available === null` answers
 * TRUE, so the control stays. See `loadAvailableProviders` for why that is the
 * safe direction.
 */
export function hasProviderToAdd(available: string[] | null, linked: string[]): boolean {
  if (available === null) return true;
  return available.some((id) => !linked.includes(id));
}
